import * as React from 'react';
import { flow, map, startCase, size, forEach, get, filter } from 'lodash';
import { II18nMessages } from '../../../../services/branding/models';
import { Button, Form } from 'react-bootstrap';
import { Field, formValueSelector, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { FaCheckCircle, FaRegCircle, FaMinus, FaInfoCircle, FaCog  } from 'react-icons/fa';
import BenefitsModal from './components/BenefitsModal';
import FieldFormControl from '../../../../components/FieldFormControl';
import Loading from '../../../../components/Loading';
import { isRequired } from '../../../../util/validators';
import { IBenefit, IProduct } from '../../../../data/api/models';
import { FormattedNumber } from 'react-intl';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { bindActionCreators } from 'redux';
import * as moment from 'moment';
import SignPostingText from './components/SignPostingText';

interface ISchemeChoiceForm extends InjectedFormProps {
  i18n: II18nMessages;
  quote: IQuoteState;
  tripType: string;
  pristine: boolean;
  submitting: boolean;
  onBack: () => void;
  renderDefinition(name: string): any;
  product: IProduct;
  schemeId: string;
  actions: {
    quote: IQuoteActions;
    submit(name: string);
  };
}

class SchemeChoiceForm extends React.Component<ISchemeChoiceForm, any> {
  public componentWillMount() {
    const { actions } = this.props;
    const steps = ['tripDetails', 'medicalDeclaration', 'additionalCover', 'schemeChoice'];
    actions.quote.updateSteps(steps);
  }

  private getBenefit = (benefits: IBenefit[], displayName) => {
    let displayBenefit = '';

    benefits.forEach((benefit) => {
      if (benefit.displayName === displayName) {
        displayBenefit = benefit.text;
      }
    });

    return displayBenefit;
  }

  private canShowKey = (key: string) => {
    const { tripType } = this.props;

    return tripType === 'single' || tripType === 'longstay' || (tripType === 'annual' && key === 'annual');
  }

  private standardiseTheName = (name) => startCase(name.replace('-', ' '));

  // name, key
  // premium, key

  private renderTripTypeColumn = (tripTypes) => (name, key) => {
    const { schemeId } = this.props;

    // TODO: Poke Toyin to make this standard >_>
    const type = this.standardiseTheName(name);
    const premium = tripTypes[ type ];

    if (! this.canShowKey(key)) {
      return null;
    }

    let content = (
      <span>
        <FaMinus />
      </span>
    );

    if (premium && premium.gross) {
      content = (
        <div>
          <h3>
            <FormattedNumber
              value={premium.gross}
              style="currency"
              currency={premium.currency}
            />
          </h3>
          <Field
            name="schemeId"
            type="radio"
            value={premium.scheme.id}
            className="select-quote"
            component={FieldFormControl}
            validate={[ isRequired ]}
          >
              <div className="select-quote-btn" >
                {premium.scheme.id === schemeId ? 'selected' : 'select'}
              <FaRegCircle className="unchecked"/>
              <FaCheckCircle className="checked"/>
            </div>
          </Field>
        </div>
      );
    }

    return (
      <div className="column annual" key={key}>
        {content}
      </div>
    );
  }

  private renderTripTypeColumnHeader = (name, key) => {
    if (! this.canShowKey(key)) {
      return null;
    }

    return (
      <div className="column annual" key={key}>
        {name}
      </div>
    );
  }

  private renderErrorMessage = (medicalErrors: string[]) => {
    const { quote, tripType: tripTypeValue, product } = this.props;
    const quotes = quote.quotes;

    let errorMessage = null;

    forEach(quotes, (item) => {
      let benefitsKey = this.standardiseTheName(product.metadata.trip_type[tripTypeValue]);

      if (size(item) === 1) {
        benefitsKey = Object.keys(item)[0];
      }

      errorMessage = item[benefitsKey].errors;
    });

    if (errorMessage.length > 0) {
      if (errorMessage.includes('Exceeds maximum medical score') || medicalErrors.length > 0) {
        return (
          <div className="medical-message-row">
            <p>Based on the information provided we are unable to provide cover. Please contact us via email on info@saltinsurance.com or telephone 01342 843560 for further information.</p>
          </div>
          );
      }

      // Message to show for anything other than medical
      return (
        <div className="medical-message-row">
          <p>Sorry, we are unable to provide you with a quote.</p>
        </div>
      );
    }

    if (errorMessage.length === 0) {
      return null;
    }
  }

  private renderQuotes = () => {
    const { i18n, quote, product, tripType: tripTypeValue } = this.props;
    const quotes = quote.quotes;

    return map(quotes, (tripTypes, level) => {
      let benefitsKey = this.standardiseTheName(product.metadata.trip_type[tripTypeValue]);

      if (size(tripTypes) === 1) {
        benefitsKey = Object.keys(tripTypes)[0];
      }

      const benefits = tripTypes[benefitsKey].benefits;
      const medicalMessage = tripTypes[benefitsKey].information && tripTypes[benefitsKey].information.medical
        ? tripTypes[benefitsKey].information.medical
        : [];

      return (
        <div key={level}>
          <div className="body-row">
            <div className="column level">
              {level}
            </div>

            <div className="column cancellation">
              {this.getBenefit(benefits, 'Cancellation')}
            </div>

            <div className="column medical">
              {this.getBenefit(benefits, 'Medical')}
            </div>

            <div className="column benefits">
              <div className="info-btn">
                <BenefitsModal
                  i18n={i18n}
                  benefits={benefits}
                />
              </div>
            </div>

            {map(product.metadata.trip_type, this.renderTripTypeColumn(tripTypes))}
          </div>
          {this.renderErrorMessage(medicalMessage)}
        </div>
      );
    });
  }

  public renderLoader() {
    return (
      <div className="schemes-loader text-center">
        <Loading />
        <p>
          Please wait whilst we generate your premiums...
        </p>
      </div>
    );
  }

  private checkForUpsell = () => {
    const { schemeId, quote, tripType, actions, dispatch, change, channel } = this.props;

    forEach(quote.quotes, (quote, key) => {
      const singleTripQuote = get(quote, 'Single Trip', null);
      const annualMultiTripQuotes = get(quote, 'Annual Multi Trip', null);

      if (singleTripQuote && tripType === 'single' && schemeId === singleTripQuote.scheme.id || tripType === 'annual') {
        actions.submit('quote');
      }

      if (tripType === 'longstay') {
        actions.submit('quote');
      }

      if (annualMultiTripQuotes && tripType === 'single' && schemeId === annualMultiTripQuotes.scheme.id) {
        dispatch(change('tripType', annualMultiTripQuotes.information.tripType));
        dispatch(change('startDate', moment().format('DD/MM/YYYY')));
        dispatch(change('maxTripDuration', annualMultiTripQuotes.information.maxTripDuration));
        dispatch(change('region', annualMultiTripQuotes.information.region));
        actions.quote.saveQuote(true);
        actions.submit('quote');
      }
    });
  }

  public render() {
    const { i18n, handleSubmit, onBack, quote, submitting, valid, product, submitFailed,
            tripType: tripTypeValues, schemeId } = this.props;

    if (quote.isCalculating || !quote.quotes) {
      return this.renderLoader();
    }
    const quoteKey = Object.keys(quote.quotes)[0];
    const quotes = get(quote.quotes, quoteKey);
    const signPost = quote.signpost;
    let benefitsKey = this.standardiseTheName(get(product, `metadata.trip_type.${tripTypeValues}`, ''));

    if (size(quotes) === 1) {
      benefitsKey = Object.keys(quotes)[0];
    }

    const NoCoverChoice = quotes && get(quotes , `${benefitsKey}.gross`, 0) === 0;

    return (
      <Form horizontal={true} onSubmit={handleSubmit}>
        <div className="section">
          <h3>{i18n.quote.schemeChoice.title}</h3>
          <p>
            Below is a summary of the cover we can provide. Remember to compare the levels of cover as well as the
            price before making a decision. Click the <FaInfoCircle /> to view the full benefits for each cover type.
            Once you have chosen a policy click select to purchase your insurance cover.
          </p>

          <SignPostingText
            isCalculating={quote.isCalculating}
            content={signPost}
          />

          <div className="available-quotes">
            <div className="header">
              <div className="column level">
                {i18n.quote.schemeChoice.level}
              </div>
              <div className="column cancellation">
                {i18n.quote.schemeChoice.cancellation}
              </div>
              <div className="column medical">
                {i18n.quote.schemeChoice.medical}
              </div>
              <div className="column benefits">
                {i18n.quote.schemeChoice.benefits}
              </div>

              {map(product.metadata.trip_type, this.renderTripTypeColumnHeader)}
            </div>

            <div className="table">
              {this.renderQuotes()}
            </div>
          </div>

        </div>
        <div className="btn-bar">
          <Button
            bsStyle="primary"
            type="button"
            onClick={onBack}
          >
            Back
          </Button>
          <div className="pull-right">
            <Button
              bsStyle="primary"
              type="button"
              onClick={this.checkForUpsell}
              disabled={submitting || (submitFailed && !valid) || NoCoverChoice || !schemeId}
            >
              {submitting ? <FaCog className="fa-spin" /> : 'Next'}
            </Button>
          </div>
        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const quote = state.quote;
      const tripType = selector(state, 'tripType');
      const schemeId = selector(state, 'schemeId');

      return {
        quote,
        tripType,
        schemeId,
      };
    },
    (dispatch) => ({
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        submit: bindActionCreators(submit, dispatch),
      },
    }),
  ),
])(SchemeChoiceForm);
